import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  ChartLine, 
  Star, 
  Envelope, 
  ChatCircle,
  Users,
  TrendUp,
  TrendDown,
  CheckCircle,
  Eye,
  Clock,
  Smiley,
  SmileyMeh,
  Funnel,
  MagnifyingGlass,
  CaretDown,
  SpinnerGap
} from "phosphor-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReviewAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [entitled, setEntitled] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  
  // Requests list state
  const [requests, setRequests] = useState([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    sentiment: "",
    channel: "",
    search: ""
  });
  const [skip, setSkip] = useState(0);
  const limit = 10;

  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    if (entitled) {
      fetchRequests();
    }
  }, [entitled, filters, skip]);

  const fetchDashboard = async () => {
    try {
      const response = await fetch(`${API}/tenants/review-analytics/dashboard`, {
        credentials: "include",
      });
      const data = await response.json();
      
      if (data.entitled === false) {
        setEntitled(false);
      } else {
        setEntitled(true);
        setDashboardData(data);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const fetchRequests = async () => {
    setRequestsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("skip", skip.toString());
      params.append("limit", limit.toString());
      if (filters.status) params.append("status", filters.status);
      if (filters.sentiment) params.append("sentiment", filters.sentiment);
      if (filters.channel) params.append("channel", filters.channel);
      if (filters.search) params.append("search", filters.search);
      
      const response = await fetch(`${API}/tenants/review-analytics/requests?${params}`, {
        credentials: "include",
      });
      const data = await response.json();
      setRequests(data.requests || []);
      setRequestsTotal(data.total || 0);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setRequestsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "bg-emerald-100 text-emerald-800";
      case "opened": return "bg-blue-100 text-blue-800";
      case "sent": return "bg-amber-100 text-amber-800";
      case "pending": return "bg-zinc-100 text-zinc-800";
      default: return "bg-zinc-100 text-zinc-800";
    }
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === "happy") return <Smiley size={18} className="text-emerald-600" weight="fill" />;
    if (sentiment === "unhappy") return <SmileyMeh size={18} className="text-amber-600" weight="fill" />;
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <SpinnerGap size={48} className="animate-spin text-[#FF6600]" />
      </div>
    );
  }

  if (!entitled) {
    return (
      <div className="min-h-screen bg-zinc-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate("/settings")}
            className="mb-6"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Settings
          </Button>
          
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardContent className="p-8 text-center">
              <ChartLine size={64} className="mx-auto mb-4 text-amber-600" />
              <h2 className="text-2xl font-heading font-bold mb-2">Review Analytics</h2>
              <p className="text-zinc-600 mb-6">
                Unlock powerful insights into your customer feedback with Review Analytics.
                Track open rates, sentiment, and conversion metrics.
              </p>
              <Button 
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                onClick={() => navigate("/billing")}
              >
                Upgrade to Access
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const { summary, funnel, sentiment_breakdown, channel_breakdown, daily_trend, recent_requests } = dashboardData || {};

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="review-analytics-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/settings")}
              className="p-2"
            >
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold uppercase flex items-center gap-2">
                <ChartLine size={28} className="text-[#FF6600]" />
                Review Analytics
              </h1>
              <p className="text-sm text-zinc-500">Track and analyze your customer review requests</p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={fetchDashboard}
            className="border-2"
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="border-2 border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Requests</p>
                  <p className="text-3xl font-bold" data-testid="total-requests">{summary?.total_requests || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#FF6600]/10 flex items-center justify-center">
                  <Envelope size={24} className="text-[#FF6600]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Last 30 Days</p>
                  <p className="text-3xl font-bold">{summary?.last_30_days || 0}</p>
                  {summary?.growth_rate !== 0 && (
                    <div className={`flex items-center text-sm ${summary?.growth_rate > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {summary?.growth_rate > 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
                      <span className="ml-1">{Math.abs(summary?.growth_rate || 0)}% vs prev</span>
                    </div>
                  )}
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Users size={24} className="text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Open Rate</p>
                  <p className="text-3xl font-bold" data-testid="open-rate">{funnel?.open_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Eye size={24} className="text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Completion Rate</p>
                  <p className="text-3xl font-bold" data-testid="completion-rate">{funnel?.completion_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle size={24} className="text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-zinc-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Satisfaction</p>
                  <p className="text-3xl font-bold" data-testid="satisfaction-rate">{sentiment_breakdown?.satisfaction_rate || 0}%</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Star size={24} className="text-emerald-600" weight="fill" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Funnel & Sentiment Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Conversion Funnel */}
          <Card className="border-2 border-zinc-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Funnel size={20} className="text-[#FF6600]" />
                Conversion Funnel
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Sent</span>
                    <span className="text-sm text-zinc-500">{funnel?.sent || 0}</span>
                  </div>
                  <div className="h-8 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-amber-500 rounded-sm" style={{width: '100%'}}></div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Opened</span>
                    <span className="text-sm text-zinc-500">{funnel?.opened || 0} ({funnel?.open_rate || 0}%)</span>
                  </div>
                  <div className="h-8 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-blue-500 rounded-sm" style={{width: `${funnel?.open_rate || 0}%`}}></div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Completed</span>
                    <span className="text-sm text-zinc-500">{funnel?.completed || 0} ({funnel?.completion_rate || 0}%)</span>
                  </div>
                  <div className="h-8 bg-zinc-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-sm" style={{width: `${funnel?.completion_rate || 0}%`}}></div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sentiment & Channel */}
          <div className="grid grid-cols-2 gap-4">
            {/* Sentiment */}
            <Card className="border-2 border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm uppercase flex items-center gap-2">
                  <Smiley size={18} className="text-[#FF6600]" />
                  Sentiment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Smiley size={20} className="text-emerald-600" weight="fill" />
                      <span className="text-sm">Happy</span>
                    </div>
                    <span className="font-bold text-emerald-600">{sentiment_breakdown?.happy || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <SmileyMeh size={20} className="text-amber-600" weight="fill" />
                      <span className="text-sm">Unhappy</span>
                    </div>
                    <span className="font-bold text-amber-600">{sentiment_breakdown?.unhappy || 0}</span>
                  </div>
                  <div className="pt-2 border-t border-zinc-100">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{sentiment_breakdown?.satisfaction_rate || 0}%</p>
                      <p className="text-xs text-zinc-500">Satisfaction Rate</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Channel */}
            <Card className="border-2 border-zinc-200">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-sm uppercase flex items-center gap-2">
                  <ChatCircle size={18} className="text-[#FF6600]" />
                  Channels
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Envelope size={18} className="text-blue-600" />
                      <span className="text-sm">Email</span>
                    </div>
                    <span className="font-bold">{channel_breakdown?.email || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ChatCircle size={18} className="text-emerald-600" />
                      <span className="text-sm">SMS</span>
                    </div>
                    <span className="font-bold">{channel_breakdown?.sms || 0}</span>
                  </div>
                  {channel_breakdown?.unknown > 0 && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Clock size={18} className="text-zinc-400" />
                        <span className="text-sm">Pending</span>
                      </div>
                      <span className="font-bold text-zinc-400">{channel_breakdown.unknown}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Requests List */}
        <Card className="border-2 border-zinc-200">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
                <Envelope size={20} className="text-[#FF6600]" />
                Review Requests
              </CardTitle>
              
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                  <Input
                    placeholder="Search..."
                    value={filters.search}
                    onChange={(e) => {
                      setFilters(f => ({...f, search: e.target.value}));
                      setSkip(0);
                    }}
                    className="pl-9 w-40"
                  />
                </div>
                
                <Select
                  value={filters.status || "all"}
                  onValueChange={(value) => {
                    setFilters(f => ({...f, status: value === "all" ? "" : value}));
                    setSkip(0);
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select
                  value={filters.sentiment || "all"}
                  onValueChange={(value) => {
                    setFilters(f => ({...f, sentiment: value === "all" ? "" : value}));
                    setSkip(0);
                  }}
                >
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Sentiment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Sentiment</SelectItem>
                    <SelectItem value="happy">Happy</SelectItem>
                    <SelectItem value="unhappy">Unhappy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {requestsLoading ? (
              <div className="flex items-center justify-center py-8">
                <SpinnerGap size={32} className="animate-spin text-[#FF6600]" />
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <Envelope size={48} className="mx-auto mb-2 opacity-30" />
                <p>No review requests found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr>
                        <th className="text-left p-3 font-medium">Customer</th>
                        <th className="text-left p-3 font-medium">Channel</th>
                        <th className="text-left p-3 font-medium">Status</th>
                        <th className="text-left p-3 font-medium">Sentiment</th>
                        <th className="text-left p-3 font-medium">Sent</th>
                        <th className="text-left p-3 font-medium">Opened</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-100">
                      {requests.map((req) => (
                        <tr key={req.review_id} className="hover:bg-zinc-50">
                          <td className="p-3">
                            <p className="font-medium">{req.customer_name}</p>
                            <p className="text-xs text-zinc-500">{req.customer_email || req.customer_phone}</p>
                          </td>
                          <td className="p-3">
                            {req.send_method === "email" && <Badge variant="outline" className="text-xs"><Envelope size={12} className="mr-1" />Email</Badge>}
                            {req.send_method === "sms" && <Badge variant="outline" className="text-xs"><ChatCircle size={12} className="mr-1" />SMS</Badge>}
                            {!req.send_method && <span className="text-zinc-400">-</span>}
                          </td>
                          <td className="p-3">
                            <Badge className={getStatusColor(req.status)}>{req.status}</Badge>
                          </td>
                          <td className="p-3">
                            {req.sentiment ? (
                              <div className="flex items-center gap-1">
                                {getSentimentIcon(req.sentiment)}
                                <span className="capitalize">{req.sentiment}</span>
                              </div>
                            ) : (
                              <span className="text-zinc-400">-</span>
                            )}
                          </td>
                          <td className="p-3 text-zinc-600">{formatDate(req.created_at)}</td>
                          <td className="p-3 text-zinc-600">{formatDate(req.opened_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {requestsTotal > limit && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-zinc-100">
                    <p className="text-sm text-zinc-500">
                      Showing {skip + 1}-{Math.min(skip + limit, requestsTotal)} of {requestsTotal}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSkip(Math.max(0, skip - limit))}
                        disabled={skip === 0}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSkip(skip + limit)}
                        disabled={skip + limit >= requestsTotal}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
